import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useSettings } from '../lib/usePublicData';

const BASE_NAV_LINKS = [
    { to: '/#programma', label: 'Programma', id: 'programma' },
    { to: '/#artiesten', label: 'Artiesten', id: 'artiesten' },
    { to: '/#familie', label: 'Familie & kids', id: 'familie', shortLabel: 'Familie' },
    { to: '/#partners', label: 'Partners', id: 'partners' },
    { to: '/#mobiliteit', label: 'Mobiliteit', id: 'mobiliteit' },
    { to: '/#contact', label: 'Contact', id: 'contact' },
];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { data: settings } = useSettings();

    const NAV_LINKS = BASE_NAV_LINKS;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <>
            <nav
                data-testid="site-nav"
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    scrolled
                        ? 'bg-cream/85 backdrop-blur-md border-b border-green/20 shadow-sm'
                        : 'bg-cream/40 backdrop-blur-sm border-b border-transparent'
                }`}
            >
                <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="nav-logo-link"
                        className="font-display text-base sm:text-lg leading-none zz-outline-title-thin"
                        aria-label="Zotte Zomer Ronse 2026 — naar homepage"
                    >
                        ZOTTE&nbsp;ZOMER
                    </Link>

                    {/* Desktop links */}
                    <ul className="hidden md:flex items-center gap-4 lg:gap-6">
                        {NAV_LINKS.map((l) => (
                            <li key={l.id}>
                                <Link
                                    to={l.to}
                                    data-testid={`nav-link-${l.id}`}
                                    className="text-[13px] lg:text-sm font-semibold tracking-wide text-[var(--green-dark)] hover:text-[var(--green)] transition-colors whitespace-nowrap"
                                >
                                    <span className="hidden lg:inline">{l.label}</span>
                                    <span className="lg:hidden">{l.shortLabel || l.label}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <div className="hidden md:block">
                        <Link
                            to="/#word-partner"
                            data-testid="nav-cta-word-partner"
                            className="zz-btn zz-btn--primary !py-2 !px-4 !text-xs"
                        >
                            Word partner
                        </Link>
                    </div>

                    {/* Mobile burger */}
                    <button
                        type="button"
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(true)}
                        className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border-2 border-[var(--green)] text-[var(--green)] bg-cream"
                        aria-label="Menu openen"
                    >
                        <Menu size={22} />
                    </button>
                </div>
            </nav>

            {/* Mobile overlay */}
            {open && (
                <div
                    data-testid="nav-mobile-overlay"
                    className="fixed inset-0 z-[60] bg-cream"
                >
                    <div className="flex items-center justify-between h-16 px-5 border-b border-green/20">
                        <Link
                            to="/"
                            data-testid="nav-mobile-logo"
                            onClick={() => setOpen(false)}
                            className="font-display text-lg zz-outline-title-thin"
                            aria-label="Naar homepage"
                        >
                            ZOTTE&nbsp;ZOMER
                        </Link>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-close"
                            className="inline-flex items-center justify-center w-11 h-11 rounded-full border-2 border-[var(--green)] text-[var(--green)]"
                            aria-label="Menu sluiten"
                        >
                            <X size={22} />
                        </button>
                    </div>
                    <ul className="px-6 py-8 space-y-2">
                        {NAV_LINKS.map((l) => (
                            <li key={l.id}>
                                <Link
                                    to={l.to}
                                    data-testid={`nav-mobile-link-${l.id}`}
                                    onClick={() => setOpen(false)}
                                    className="block py-4 font-display text-2xl zz-outline-title-dark border-b border-green/15"
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                        <li className="pt-4">
                            <Link
                                to="/#word-partner"
                                data-testid="nav-mobile-cta-word-partner"
                                onClick={() => setOpen(false)}
                                className="zz-btn zz-btn--primary w-full"
                            >
                                Word partner
                            </Link>
                        </li>
                    </ul>
                </div>
            )}
        </>
    );
};

export default Nav;
